import { EditedDocument, CpsDocumentMetadata, CONTENT_TYPE_FIELD, DOC_ID_FIELD } from '@cs-cms/types';
import React from 'react';
import { FormRetryData, DocumentFormContext } from '../contexts/DocumentFormContext';

interface DocumentFormProviderProps {
  children: React.ReactNode;
}

export const DocumentFormProvider: React.FC<DocumentFormProviderProps> = ({ children }) => {
  const [editedDocument, setEditedDocument] = React.useState<'new' | EditedDocument | undefined>(undefined);
  const [isRetry, setIsRetry] = React.useState<boolean>(false);
  const [fileUploadRequired, setFileUploadRequired] = React.useState<boolean>(false);
  const [initialFormData, setInitialFormData] = React.useState<{
    metadata?: CpsDocumentMetadata;
    contentType?: string;
  }>();

  const openNewDocumentForm = (initialMetadata?: CpsDocumentMetadata, contentType?: string) => {
    setInitialFormData(initialMetadata || contentType ? { metadata: initialMetadata, contentType } : undefined);
    setIsRetry(false);
    setEditedDocument('new');
  };

  const openEditDocumentForm = (document: EditedDocument, initialMetadata?: CpsDocumentMetadata) => {
    setInitialFormData(initialMetadata ? { metadata: initialMetadata } : undefined);
    setIsRetry(false);
    setEditedDocument(document);
  };

  const openRetryForm = (retryData: FormRetryData) => {
    setInitialFormData({
      metadata: retryData.metadata,
      contentType: retryData.contentType
    });
    setIsRetry(true);
    setFileUploadRequired(retryData.fileUploadRequired);

    if (retryData.formType === 'new') {
      setEditedDocument('new');
    } else if (retryData.documentId) {
      setEditedDocument({
        [CONTENT_TYPE_FIELD]: retryData.contentType,
        [DOC_ID_FIELD]: retryData.documentId
      });
    }
  };

  const closeDocumentForm = () => {
    setEditedDocument(undefined);
    setInitialFormData(undefined);
    setIsRetry(false);
  };

  return (
    <DocumentFormContext.Provider
      value={{
        openNewDocumentForm,
        openEditDocumentForm,
        openRetryForm,
        closeDocumentForm,
        isFormOpen: editedDocument !== undefined,
        editedDocument,
        initialFormData,
        isRetry,
        fileUploadRequired
      }}
    >
      {children}
    </DocumentFormContext.Provider>
  );
};
